import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { TrendingUp, Users, Briefcase, Brain, CreditCard, MessageSquare, Wifi, FileText, AlertTriangle, ShieldCheck, Zap, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import VerificationBadge from '../shared/VerificationBadge';

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [templatesAnalytics, setTemplatesAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [{ data: d }, { data: t }] = await Promise.all([
        api.get('/analytics/overview'),
        api.get('/templates/analytics'),
      ]);
      setData(d);
      setTemplatesAnalytics(t);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-center py-20 text-gray-500">Failed to load analytics</div>;

  const cards = [
    { icon: Briefcase, label: 'Total Cases', value: data.cases.total, sub: `${data.cases.this_week} this week`, color: 'bg-black' },
    { icon: TrendingUp, label: 'Completion Rate', value: `${data.cases.completion_rate}%`, sub: `${data.cases.completed} completed`, color: 'bg-success' },
    { icon: Users, label: 'Total Users', value: data.users.total, sub: `${data.users.clients} clients`, color: 'bg-primary' },
    { icon: Brain, label: 'Templates', value: data.templates.published, sub: `${data.templates.candidates} candidates`, color: 'bg-ai-indicator' },
    { icon: CreditCard, label: 'Revenue', value: `₹${data.revenue.total.toLocaleString()}`, sub: 'total collected', color: 'bg-black' },
    { icon: MessageSquare, label: 'Messages', value: data.messages.total, sub: 'total messages', color: 'bg-gray-700' },
    { icon: FileText, label: 'Template Driven', value: data.cases.template_driven, sub: 'cases with templates', color: 'bg-success' },
    { icon: Wifi, label: 'Live Connections', value: data.websocket_connections, sub: 'active now', color: 'bg-primary' },
  ];

  return (
    <div data-testid="admin-analytics">
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Analytics</h1>
      <p className="text-sm text-gray-500 mb-6">Platform performance overview</p>

      {/* Metrics Grid */}
      <div className="grid grid-cols-4 border border-gray-200" data-testid="analytics-metrics">
        {cards.map(({ icon: Icon, label, value, sub, color }, i) => (
          <div key={label} className={`p-5 ${i < 4 ? 'border-b' : ''} ${(i + 1) % 4 !== 0 ? 'border-r' : ''} border-gray-200`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-6 h-6 ${color} rounded-sm flex items-center justify-center`}>
                <Icon className="w-3 h-3 text-white" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{label}</span>
            </div>
            <p className="font-heading text-2xl font-bold">{value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* Case Status Breakdown */}
      <div className="mt-6 border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-200 bg-surface">
          <h3 className="font-heading text-base font-semibold">Case Pipeline</h3>
        </div>
        <div className="grid grid-cols-8 divide-x divide-gray-200">
          {Object.entries(data.cases.status_breakdown || {}).map(([status, count]) => {
            const total = data.cases.total || 1;
            const pct = Math.round((count / total) * 100);
            return (
              <div key={status} className="p-4 text-center">
                <div className="h-16 flex items-end justify-center mb-2">
                  <div className="w-8 bg-black/80 rounded-t" style={{ height: `${Math.max(pct * 0.6, 4)}px` }} />
                </div>
                <p className="font-heading text-lg font-bold">{count}</p>
                <p className="font-mono text-[9px] uppercase tracking-wider text-gray-400 mt-0.5">{status.replace(/_/g, ' ')}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Template vs Adaptive */}
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div className="border border-gray-200 p-5">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-3">Case Resolution Method</h3>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-success rounded-sm" />
                <span className="text-sm">Template-Driven</span>
              </div>
              <p className="font-heading text-3xl font-bold">{data.cases.template_driven}</p>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-ai-indicator rounded-sm" />
                <span className="text-sm">Adaptive</span>
              </div>
              <p className="font-heading text-3xl font-bold">{data.cases.adaptive_driven}</p>
            </div>
          </div>
        </div>
        <div className="border border-gray-200 p-5">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-3">This Month</h3>
          <p className="font-heading text-4xl font-bold">{data.cases.this_month}</p>
          <p className="text-sm text-gray-500 mt-1">new cases created</p>
          <p className="text-xs text-gray-400 mt-2">{data.cases.this_week} this week</p>
        </div>
      </div>

      {/* Trust & Knowledge Layer (iteration 10) */}
      {templatesAnalytics && <TrustAndKnowledge analytics={templatesAnalytics} />}
    </div>
  );
}

/* ---------------- Trust & Knowledge widgets ---------------- */

function TrustAndKnowledge({ analytics }) {
  const [a, setA] = useState(analytics);
  const [selected, setSelected] = useState(new Set());
  const [running, setRunning] = useState({});   // pack_id -> 'queued'|'running'|'passed'|'partial'|'failed'|'error'
  const [bulkSummary, setBulkSummary] = useState(null);
  const [confirmScope, setConfirmScope] = useState(null); // 'selected' | 'untested' | 'partial' | 'weak'
  const [latestResultByPack, setLatestResultByPack] = useState({}); // pack_id -> {status, completion_pct}

  useEffect(() => { setA(analytics); }, [analytics]);

  const refreshAnalytics = async () => {
    try {
      const { data } = await api.get('/templates/analytics');
      setA(data);
    } catch { /* noop */ }
  };

  const runOne = async (pack_id) => {
    setRunning(r => ({ ...r, [pack_id]: 'running' }));
    try {
      const { data } = await api.post(`/templates/packs/${pack_id}/quick-test`, {});
      const status = data.run.status;
      setRunning(r => ({ ...r, [pack_id]: status }));
      setLatestResultByPack(m => ({ ...m, [pack_id]: { status, completion_pct: data.run.completion_pct } }));
      toast.success(`Test ${status.toUpperCase()} · ${data.pack.name}`);
      await refreshAnalytics();
    } catch {
      setRunning(r => ({ ...r, [pack_id]: 'error' }));
      toast.error('Quick test failed');
    }
  };

  const runBulk = async (ids, scope = null) => {
    setBulkSummary({ running: true, total: ids?.length ?? 0, scope });
    // Mark queued
    if (ids) {
      const next = {};
      ids.forEach(id => { next[id] = 'queued'; });
      setRunning(r => ({ ...r, ...next }));
    }
    try {
      const body = scope ? { scope } : { pack_ids: ids };
      const { data } = await api.post('/templates/packs/quick-test-bulk', body, { timeout: 60000 });
      const perPack = {};
      const latest = {};
      (data.results || []).forEach(r => {
        perPack[r.pack_id] = r.status;
        latest[r.pack_id] = { status: r.status, completion_pct: r.completion_pct };
      });
      setRunning(r => ({ ...r, ...perPack }));
      setLatestResultByPack(m => ({ ...m, ...latest }));
      setBulkSummary({ ...data, running: false, scope });
      setSelected(new Set());
      await refreshAnalytics();
      toast.success(`Bulk test: ${data.summary.passed} passed · ${data.summary.partial} partial · ${data.summary.failed} failed`);
    } catch (err) {
      toast.error('Bulk test failed');
      setBulkSummary({ running: false, error: true });
    }
  };

  const confirmAndRun = (mode) => {
    if (mode === 'selected') {
      if (selected.size === 0) return toast.error('Select templates first');
      runBulk(Array.from(selected));
    } else {
      runBulk(null, mode);
    }
    setConfirmScope(null);
  };

  const totalRuns = a.total_runs || 0;
  const passRate = Math.round((a.pass_rate || 0) * 100);
  const fallbackRate = Math.round((a.fallback_rate || 0) * 100);
  const weakRows = a.weak_verification_used_in_runtime || [];

  const toggleSelect = (id) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === weakRows.length) setSelected(new Set());
    else setSelected(new Set(weakRows.map(r => r.template_id)));
  };

  return (
    <div className="mt-8 space-y-6" data-testid="trust-knowledge">
      <div className="flex items-center gap-2 mt-2">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <h2 className="font-mono text-[11px] uppercase tracking-widest text-gray-700">
          Trust & Knowledge · Runtime
        </h2>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <TrustStat label="Pass rate" value={`${passRate}%`} sub={`${a.runs_by_status?.passed || 0} / ${totalRuns} runs`} tone={passRate >= 70 ? 'green' : passRate >= 40 ? 'orange' : 'red'} />
        <TrustStat label="Fallback rate" value={`${fallbackRate}%`} sub={`${a.fallback_count || 0} / ${a.total_cases || 0} cases`} tone={fallbackRate < 20 ? 'green' : fallbackRate < 50 ? 'orange' : 'red'} />
        <TrustStat label="Override publishes" value={a.overridden_publishes || 0} sub="published without verification" tone={(a.overridden_publishes || 0) === 0 ? 'green' : 'orange'} />
        <TrustStat label="Published untested" value={a.published_but_never_tested || 0} sub="live but never sandboxed" tone={(a.published_but_never_tested || 0) === 0 ? 'green' : 'red'} />
      </div>

      {/* Remediation loop — this is the new interactive block */}
      <RemediationTable
        rows={weakRows}
        selected={selected}
        running={running}
        latestResultByPack={latestResultByPack}
        onToggleSelect={toggleSelect}
        onToggleAll={toggleAll}
        onRunOne={runOne}
        onBulkClick={setConfirmScope}
        bulkSummary={bulkSummary}
      />

      {confirmScope && (
        <BulkConfirmDialog
          scope={confirmScope}
          selectedCount={selected.size}
          onCancel={() => setConfirmScope(null)}
          onConfirm={() => confirmAndRun(confirmScope)}
        />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Most used templates */}
        <section className="border border-gray-200 bg-white" data-testid="most-used-templates">
          <header className="px-5 py-3 border-b border-gray-200 bg-surface">
            <h3 className="font-mono text-[11px] uppercase tracking-widest text-gray-700">Most-used templates in runtime</h3>
          </header>
          <ul className="divide-y divide-gray-100">
            {(a.most_used_templates || []).length === 0 && (
              <li className="p-5 text-sm text-gray-400">No runtime usage yet.</li>
            )}
            {(a.most_used_templates || []).map(m => (
              <li key={m.template_id} className="px-5 py-3 flex items-center gap-3">
                <span className="w-10 text-right font-mono text-sm font-semibold">{m.use_count}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[11px] font-mono text-gray-500">{m.family}{m.variant ? ` · ${m.variant}` : ''}</span>
                    <VerificationBadge status={m.verification_status} />
                    {m.published_with_override && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-orange-50 text-orange-700 border border-orange-200 uppercase tracking-widest">
                        <AlertTriangle className="w-2.5 h-2.5" /> override
                      </span>
                    )}
                    {typeof m.fallback_rate === 'number' && m.fallback_rate > 0 && (
                      <span className="text-[10px] font-mono text-red-600">fallback {Math.round(m.fallback_rate * 100)}%</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Top failed questions */}
        <section className="border border-gray-200 bg-white" data-testid="top-failed-questions">
          <header className="px-5 py-3 border-b border-gray-200 bg-surface">
            <h3 className="font-mono text-[11px] uppercase tracking-widest text-gray-700">Questions most frequently left unanswered</h3>
          </header>
          <ul className="divide-y divide-gray-100">
            {(a.top_failed_questions || []).length === 0 && (
              <li className="p-5 text-sm text-gray-400">No test-run data yet.</li>
            )}
            {(a.top_failed_questions || []).map((q, i) => (
              <li key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="w-10 text-right font-mono text-sm font-semibold text-red-500">{q.count}</span>
                <p className="text-sm text-gray-700 flex-1 truncate">{q.question}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Runtime source breakdown */}
      <section className="border border-gray-200 bg-white" data-testid="runtime-source-breakdown">
        <header className="px-5 py-3 border-b border-gray-200 bg-surface">
          <h3 className="font-mono text-[11px] uppercase tracking-widest text-gray-700">Runtime source breakdown</h3>
        </header>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { key: 'template-driven', label: 'Template-driven', tone: 'bg-success' },
            { key: 'hybrid', label: 'Hybrid', tone: 'bg-primary' },
            { key: 'adaptive-only', label: 'Adaptive-only', tone: 'bg-ai-indicator' },
            { key: 'unresolved', label: 'Pre-intake', tone: 'bg-gray-500' },
          ].map(s => {
            const count = a.runtime_source_breakdown?.[s.key] || 0;
            return (
              <div key={s.key} className="border border-gray-100 p-3 rounded">
                <div className={`w-2 h-2 ${s.tone} rounded-full mx-auto mb-1.5`} />
                <p className="font-heading text-2xl font-bold">{count}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mt-1">{s.label}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ---------------- Remediation Table ---------------- */

function RemediationTable({ rows, selected, running, latestResultByPack, onToggleSelect, onToggleAll, onRunOne, onBulkClick, bulkSummary }) {
  const hasRows = rows && rows.length > 0;
  const allSelected = hasRows && selected.size === rows.length;
  return (
    <section className="border border-orange-200 bg-white" data-testid="remediation-table">
      <header className="px-5 py-3 border-b border-orange-200 bg-orange-50 flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-4 h-4 text-orange-700" />
        <h3 className="font-mono text-[11px] uppercase tracking-widest text-orange-800">
          Weak-verification templates in runtime · remediate now
        </h3>
        <span className="text-[11px] font-mono text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-sm">
          {rows.length} templates
        </span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button
            data-testid="bulk-test-selected"
            onClick={() => onBulkClick('selected')}
            disabled={selected.size === 0}
            className="text-xs px-3 py-1.5 border border-orange-300 text-orange-800 hover:bg-orange-100 disabled:opacity-40 transition flex items-center gap-1.5"
          >
            <Zap className="w-3 h-3" /> Run selected ({selected.size})
          </button>
          <button
            data-testid="bulk-test-untested"
            onClick={() => onBulkClick('untested')}
            className="text-xs px-3 py-1.5 border border-gray-300 hover:bg-gray-50 transition"
          >
            Test all untested
          </button>
          <button
            data-testid="bulk-test-partial"
            onClick={() => onBulkClick('partial')}
            className="text-xs px-3 py-1.5 border border-gray-300 hover:bg-gray-50 transition"
          >
            Test all partial
          </button>
          <button
            data-testid="bulk-test-weak"
            onClick={() => onBulkClick('weak')}
            className="text-xs px-3 py-1.5 bg-primary text-white rounded hover:bg-primary-hover transition flex items-center gap-1.5"
          >
            <Zap className="w-3 h-3" /> Run all weak
          </button>
        </div>
      </header>

      {bulkSummary && !bulkSummary.running && !bulkSummary.error && (
        <div className="px-5 py-2.5 bg-green-50 border-b border-green-200 flex items-center gap-3 text-xs" data-testid="bulk-summary">
          <CheckCircle2 className="w-4 h-4 text-success" />
          <span className="font-mono">BULK COMPLETE</span>
          <span>· {bulkSummary.total} total</span>
          <span className="text-success">· {bulkSummary.summary?.passed || 0} passed</span>
          <span className="text-orange-700">· {bulkSummary.summary?.partial || 0} partial</span>
          <span className="text-red-600">· {bulkSummary.summary?.failed || 0} failed</span>
          {(bulkSummary.summary?.error || 0) > 0 && <span className="text-red-700">· {bulkSummary.summary.error} errors</span>}
        </div>
      )}

      {bulkSummary?.running && (
        <div className="px-5 py-2.5 bg-blue-50 border-b border-blue-200 flex items-center gap-3 text-xs" data-testid="bulk-running">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="font-mono">Running bulk test…</span>
          <span>· {bulkSummary.total || 'all'} templates</span>
        </div>
      )}

      {!hasRows ? (
        <div className="p-10 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-success" />
          <p className="text-sm text-gray-600">No weak-verification templates running in live runtime.</p>
          <p className="text-xs text-gray-400 mt-1">Every template touching a client is passed or not yet used.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-surface">
                <th className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    data-testid="select-all"
                    checked={allSelected}
                    onChange={onToggleAll}
                    aria-label="Select all"
                  />
                </th>
                <Th>Template</Th>
                <Th>Family</Th>
                <Th>Variant</Th>
                <Th>Verification</Th>
                <Th className="text-right">Runtime use</Th>
                <Th className="text-right">Fallback</Th>
                <Th>Latest test</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isSel = selected.has(r.template_id);
                const runState = running[r.template_id];
                const latest = latestResultByPack[r.template_id];
                return (
                  <tr
                    key={r.template_id}
                    className="border-b border-gray-100 hover:bg-surface/50 transition"
                    data-testid={`remediation-row-${r.template_id}`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        data-testid={`select-${r.template_id}`}
                        checked={isSel}
                        onChange={() => onToggleSelect(r.template_id)}
                      />
                    </td>
                    <td className="px-3 py-3 text-sm font-medium">
                      {r.name}
                      {r.published_with_override && (
                        <div className="mt-0.5 flex items-center gap-1 text-[10px] font-mono text-orange-700">
                          <AlertTriangle className="w-3 h-3" /> override publish
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">{r.family}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">{r.variant || '—'}</td>
                    <td className="px-3 py-3">
                      <VerificationBadge status={r.verification_status} />
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-sm font-semibold">{r.use_count}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {typeof r.fallback_rate === 'number' ? `${Math.round(r.fallback_rate * 100)}%` : '—'}
                      <div className="text-[10px] text-gray-400">{r.fallback_count || 0} cases</div>
                    </td>
                    <td className="px-3 py-3">
                      <RunStatusBadge
                        state={runState}
                        latest={latest}
                        lastTestedAt={r.last_tested_at}
                      />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          data-testid={`run-test-${r.template_id}`}
                          onClick={() => onRunOne(r.template_id)}
                          disabled={runState === 'running' || runState === 'queued'}
                          className="text-xs px-2.5 py-1 border border-primary text-primary hover:bg-primary hover:text-white transition disabled:opacity-50 flex items-center gap-1"
                        >
                          {runState === 'running' || runState === 'queued'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Zap className="w-3 h-3" />}
                          Run test
                        </button>
                        <a
                          data-testid={`view-details-${r.template_id}`}
                          href={`/admin/templates/${r.template_id}`}
                          className="text-xs px-2.5 py-1 border border-gray-300 hover:bg-gray-50 transition"
                        >
                          Details
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 text-left ${className}`}>
      {children}
    </th>
  );
}

function RunStatusBadge({ state, latest, lastTestedAt }) {
  // In-flight states from current session
  if (state === 'queued') {
    return <Pill tone="gray" icon={Clock}>Queued</Pill>;
  }
  if (state === 'running') {
    return <Pill tone="blue" icon={Loader2} spin>Running</Pill>;
  }
  if (state === 'error') {
    return <Pill tone="red" icon={XCircle}>Error</Pill>;
  }
  // Post-run result in this session
  const resultState = state || latest?.status;
  if (resultState === 'passed') {
    return (
      <Pill tone="green" icon={CheckCircle2}>
        Passed{latest?.completion_pct != null ? ` · ${latest.completion_pct}%` : ''}
      </Pill>
    );
  }
  if (resultState === 'partial') {
    return <Pill tone="orange" icon={AlertTriangle}>Partial{latest?.completion_pct != null ? ` · ${latest.completion_pct}%` : ''}</Pill>;
  }
  if (resultState === 'failed') {
    return <Pill tone="red" icon={XCircle}>Failed</Pill>;
  }
  // Nothing run in this session — show last tested or dash
  if (lastTestedAt) {
    return <span className="text-[11px] font-mono text-gray-500">last tested {new Date(lastTestedAt).toLocaleDateString()}</span>;
  }
  return <span className="text-[11px] font-mono text-gray-400">never tested</span>;
}

function Pill({ children, tone = 'gray', icon: Icon, spin }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border rounded-sm ${tones[tone]}`}>
      {Icon && <Icon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />}
      {children}
    </span>
  );
}

function BulkConfirmDialog({ scope, selectedCount, onCancel, onConfirm }) {
  const titles = {
    selected: `Run synthetic tests on ${selectedCount} selected template${selectedCount === 1 ? '' : 's'}?`,
    untested: 'Run synthetic tests on ALL untested published templates?',
    partial: 'Run synthetic tests on ALL partially-verified published templates?',
    weak: 'Run synthetic tests on ALL weak-verification published templates (untested + partial + override)?',
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="bulk-confirm-dialog">
      <div className="bg-white border border-gray-200 rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        <header className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">{titles[scope]}</h3>
          </div>
        </header>
        <div className="p-5 space-y-3 text-sm text-gray-700">
          <p>Each test will:</p>
          <ul className="space-y-1.5 pl-5 list-disc text-gray-600">
            <li>create a persisted <span className="font-mono">TemplateTestRun</span> row (audit trail)</li>
            <li>auto-answer each required question with a realistic synthetic value</li>
            <li>update the pack's <span className="font-mono">verification_status</span></li>
            <li>roll into runtime analytics immediately</li>
          </ul>
          <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">
            <strong>Will NOT</strong> auto-publish, auto-edit, or modify template logic.
            Admin still decides what to do after reviewing results.
          </p>
        </div>
        <footer className="px-5 py-3 bg-surface border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            data-testid="bulk-cancel-btn"
            onClick={onCancel}
            className="px-4 py-1.5 text-sm border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            data-testid="bulk-confirm-btn"
            onClick={onConfirm}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-1.5"
          >
            <Zap className="w-3.5 h-3.5" /> Run tests
          </button>
        </footer>
      </div>
    </div>
  );
}

function TrustStat({ label, value, sub, tone = 'default' }) {
  const tones = {
    default: 'bg-white border-gray-200',
    green: 'bg-green-50 border-green-200',
    orange: 'bg-orange-50 border-orange-200',
    red: 'bg-red-50 border-red-200',
  };
  return (
    <div className={`border ${tones[tone]} p-4 rounded`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</p>
      <p className="font-heading text-2xl font-bold">{value}</p>
      <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
}
